import { useState, useEffect, useCallback, useRef } from 'react';
import { useKeycloak } from '@react-keycloak/web';

export interface CoCounsel {
  id: string;
  name: string;
  firm: string;
  email: string;
  phone: string;
  role: 'lead' | 'associate' | 'consultant' | 'expert';
  relationship: 'primary' | 'secondary' | 'consulting';
  startDate: string;
  endDate?: string;
  feeSplit?: number;
  notes: string;
  status: 'active' | 'inactive' | 'pending';
}

export interface CreateCoCounselData {
  name: string;
  firm: string;
  email?: string;
  phone?: string;
  role?: string;
  relationship?: string;
  status?: string;
  start_date: string;
  end_date?: string;
  fee_split?: number;
  notes?: string;
}

export interface UpdateCoCounselData {
  name?: string;
  firm?: string;
  email?: string;
  phone?: string;
  role?: string;
  relationship?: string;
  status?: string;
  start_date?: string;
  end_date?: string | null;
  fee_split?: number | null;
  notes?: string | null;
}

export function useCoCounsel(clientId: string) {
  const { keycloak } = useKeycloak();
  const [coCounsel, setCoCounsel] = useState<CoCounsel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [feeSplitTotal, setFeeSplitTotal] = useState(0);
  const [total, setTotal] = useState(0);

  const clientIdRef = useRef(clientId);
  clientIdRef.current = clientId;

  const getHeaders = useCallback(() => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${keycloak?.token || ''}`,
  }), [keycloak?.token]);

  const fetchAll = useCallback(async () => {
    if (!clientId || !keycloak?.token) return;

    setLoading(true);
    setError(null);

    try {
      const res = await fetch(
        `/api/v1/clients/${clientId}/co-counsel`,
        { headers: getHeaders() }
      );

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.description || body.message || `Failed to fetch co-counsel: ${res.statusText}`);
      }

      const json = await res.json();

      if (clientIdRef.current !== clientId) return;

      setCoCounsel(json.data || []);
      setTotal(json.total || 0);
      setFeeSplitTotal(json.feeSplitTotal || 0);
    } catch (err) {
      if (clientIdRef.current !== clientId) return;
      setError(err instanceof Error ? err.message : 'Failed to fetch co-counsel');
    } finally {
      if (clientIdRef.current === clientId) {
        setLoading(false);
      }
    }
  }, [clientId, keycloak?.token, getHeaders]);

  const addCoCounsel = useCallback(async (data: CreateCoCounselData): Promise<CoCounsel | null> => {
    if (!clientId || !keycloak?.token) return null;

    try {
      const res = await fetch(
        `/api/v1/clients/${clientId}/co-counsel`,
        {
          method: 'POST',
          headers: getHeaders(),
          body: JSON.stringify(data),
        }
      );

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.description || body.message || `Failed to add co-counsel: ${res.statusText}`);
      }

      const json = await res.json();
      await fetchAll();
      return json.data;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to add co-counsel';
      setError(msg);
      throw err;
    }
  }, [clientId, keycloak?.token, getHeaders, fetchAll]);

  const updateCoCounsel = useCallback(async (id: string, data: UpdateCoCounselData): Promise<CoCounsel | null> => {
    if (!clientId || !keycloak?.token) return null;

    try {
      const res = await fetch(
        `/api/v1/clients/${clientId}/co-counsel/${id}`,
        {
          method: 'PUT',
          headers: getHeaders(),
          body: JSON.stringify(data),
        }
      );

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.description || body.message || `Failed to update co-counsel: ${res.statusText}`);
      }

      const json = await res.json();
      await fetchAll();
      return json.data;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to update co-counsel';
      setError(msg);
      throw err;
    }
  }, [clientId, keycloak?.token, getHeaders, fetchAll]);

  const removeCoCounsel = useCallback(async (id: string): Promise<void> => {
    if (!clientId || !keycloak?.token) return;

    try {
      const res = await fetch(
        `/api/v1/clients/${clientId}/co-counsel/${id}`,
        {
          method: 'DELETE',
          headers: getHeaders(),
        }
      );

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.description || body.message || `Failed to remove co-counsel: ${res.statusText}`);
      }

      await fetchAll();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to remove co-counsel';
      setError(msg);
      throw err;
    }
  }, [clientId, keycloak?.token, getHeaders, fetchAll]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  return {
    coCounsel,
    loading,
    error,
    total,
    feeSplitTotal,
    addCoCounsel,
    updateCoCounsel,
    removeCoCounsel,
    refresh: fetchAll,
  };
}
